import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Upload, Check, Clock, XCircle, Loader2, Building2, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DOC_LABELS = {
  id_document: 'Documento d\'identità',
  fiscal_code: 'Codice Fiscale / Tax ID',
  proof_of_address: 'Prova d\'indirizzo',
  business_certificate: 'Certificato di costituzione azienda',
};

const STATUS_MAP = {
  pending: { label: 'In attesa di verifica', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock },
  approved: { label: 'Approvato', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: Check },
  rejected: { label: 'Rifiutato', color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle },
};

export default function ComplianceUpload({ countryCode, countryName, onStatusChange }) {
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requesterType, setRequesterType] = useState('');
  const [form, setForm] = useState({ requester_name: '', requester_fiscal_code: '', proof_of_address_date: '' });
  const [files, setFiles] = useState({ id_document: null, fiscal_code_doc: null, proof_of_address: null, business_certificate: null });

  useEffect(() => {
    if (countryCode) checkStatus();
  }, [countryCode]);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/compliance/country/${countryCode}`);
      setComplianceStatus(res.data);
      if (onStatusChange) onStatusChange(res.data);
    } catch (err) { console.error('Compliance check error:', err); }
    finally { setLoading(false); }
  };

  const handleFileChange = (key, file) => {
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 10 MB.');
      return;
    }
    setFiles(prev => ({ ...prev, [key]: file }));
  };

  const handleSubmit = async () => {
    if (!requesterType) { toast.error('Seleziona il tipo di richiedente'); return; }
    if (!form.requester_name.trim()) { toast.error('Nome richiedente obbligatorio'); return; }
    if (!form.proof_of_address_date) { toast.error('Data prova d\'indirizzo obbligatoria'); return; }
    if (!files.id_document) { toast.error('Documento d\'identità obbligatorio'); return; }
    if (!files.proof_of_address) { toast.error('Prova d\'indirizzo obbligatoria'); return; }
    if (requesterType === 'business' && !files.business_certificate) {
      toast.error('Certificato azienda obbligatorio per le aziende');
      return;
    }

    // Validate date within 90 days
    const poaDate = new Date(form.proof_of_address_date);
    const daysDiff = Math.floor((new Date() - poaDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) { toast.error(`La prova d'indirizzo è datata ${daysDiff} giorni fa. Deve essere entro 90 giorni.`); return; }
    if (daysDiff < 0) { toast.error('La data non può essere nel futuro.'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('country_code', countryCode);
      formData.append('requester_type', requesterType);
      formData.append('requester_name', form.requester_name);
      formData.append('requester_fiscal_code', form.requester_fiscal_code);
      formData.append('proof_of_address_date', form.proof_of_address_date);
      formData.append('id_document', files.id_document);
      formData.append('proof_of_address', files.proof_of_address);
      if (files.fiscal_code_doc) formData.append('fiscal_code_doc', files.fiscal_code_doc);
      if (files.business_certificate) formData.append('business_certificate', files.business_certificate);

      const res = await api.post('/compliance/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      checkStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio documenti');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
    </div>
  );

  // Already approved
  if (complianceStatus?.approved) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="compliance-approved">
        <Check className="w-5 h-5 text-emerald-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-700">Documenti approvati per {countryName}</p>
          <p className="text-xs text-emerald-600">Intestatario: {complianceStatus.requester_name} ({complianceStatus.requester_type === 'business' ? 'Azienda' : 'Privato'})</p>
        </div>
      </div>
    );
  }

  // Expired
  if (complianceStatus?.expired) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="compliance-expired">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm font-medium text-amber-700">Prova d'indirizzo scaduta per {countryName}</p>
        </div>
        <p className="text-xs text-amber-600 mb-3">La prova d'indirizzo ha superato i 90 giorni. Carica un documento aggiornato.</p>
        {/* Show the form below to re-submit */}
      </div>
    );
  }

  // Pending
  if (complianceStatus?.pending) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="compliance-pending">
        <Clock className="w-5 h-5 text-amber-600 shrink-0 animate-pulse" />
        <div>
          <p className="text-sm font-medium text-amber-700">Documenti in attesa di verifica per {countryName}</p>
          <p className="text-xs text-amber-600">Riceverai una notifica email dopo la revisione.</p>
        </div>
      </div>
    );
  }

  // Need to submit documents
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="compliance-form">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-5 h-5 text-sky-500" />
        <h3 className="font-semibold text-slate-800">Documenti richiesti per {countryName}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">Per acquistare numeri telefonici è necessario verificare l'identità del richiedente.</p>

      {/* Requester Type */}
      <div className="mb-5">
        <Label className="text-sm font-medium mb-2 block">Tipo richiedente</Label>
        <div className="flex gap-3">
          <button onClick={() => setRequesterType('private')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition flex-1 ${
              requesterType === 'private' ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-200'
            }`} data-testid="type-private">
            <User className="w-4 h-4" /> Privato
          </button>
          <button onClick={() => setRequesterType('business')}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition flex-1 ${
              requesterType === 'business' ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-600 hover:border-sky-200'
            }`} data-testid="type-business">
            <Building2 className="w-4 h-4" /> Azienda
          </button>
        </div>
      </div>

      {requesterType && (
        <div className="space-y-4">
          {/* Requester Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome completo {requesterType === 'business' ? '/ Ragione sociale' : ''} *</Label>
              <Input value={form.requester_name} onChange={e => setForm(f => ({ ...f, requester_name: e.target.value }))}
                placeholder={requesterType === 'business' ? 'TelcaVoIP S.r.l.' : 'Mario Rossi'} data-testid="requester-name" />
            </div>
            <div>
              <Label className="text-xs">Codice Fiscale / P.IVA / Tax ID</Label>
              <Input value={form.requester_fiscal_code} onChange={e => setForm(f => ({ ...f, requester_fiscal_code: e.target.value }))}
                placeholder="RSSMRA80A01H501Z" data-testid="fiscal-code" />
            </div>
          </div>

          {/* Document uploads */}
          <div className="space-y-3">
            {/* ID Document */}
            <div>
              <Label className="text-xs font-medium">Documento d'identità *</Label>
              <p className="text-[11px] text-slate-400 mb-1">Carta d'identità, passaporto o patente (PDF, JPG, PNG)</p>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleFileChange('id_document', e.target.files[0])}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sky-50 file:text-sky-700 file:font-medium file:cursor-pointer hover:file:bg-sky-100"
                data-testid="upload-id" />
            </div>

            {/* Fiscal Code Doc (optional file) */}
            <div>
              <Label className="text-xs font-medium">Documento Codice Fiscale / Tax ID</Label>
              <p className="text-[11px] text-slate-400 mb-1">Tessera sanitaria, certificato CF (opzionale se già indicato sopra)</p>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleFileChange('fiscal_code_doc', e.target.files[0])}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-50 file:text-slate-600 file:font-medium file:cursor-pointer hover:file:bg-slate-100"
                data-testid="upload-fiscal" />
            </div>

            {/* Proof of Address */}
            <div>
              <Label className="text-xs font-medium">Prova d'indirizzo *</Label>
              <p className="text-[11px] text-slate-400 mb-1">Bolletta (telefono, luce, gas) o estratto conto bancario. Deve riportare il nome del richiedente e non essere più vecchia di 90 giorni.</p>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleFileChange('proof_of_address', e.target.files[0])}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sky-50 file:text-sky-700 file:font-medium file:cursor-pointer hover:file:bg-sky-100"
                data-testid="upload-poa" />
              <div className="mt-2">
                <Label className="text-xs">Data del documento *</Label>
                <Input type="date" value={form.proof_of_address_date} onChange={e => setForm(f => ({ ...f, proof_of_address_date: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]} data-testid="poa-date" />
              </div>
            </div>

            {/* Business Certificate */}
            {requesterType === 'business' && (
              <div>
                <Label className="text-xs font-medium">Certificato di costituzione azienda *</Label>
                <p className="text-[11px] text-slate-400 mb-1">Visura camerale, certificato CCIAA o equivalente</p>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => handleFileChange('business_certificate', e.target.files[0])}
                  className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sky-50 file:text-sky-700 file:font-medium file:cursor-pointer hover:file:bg-sky-100"
                  data-testid="upload-business-cert" />
              </div>
            )}
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-sky-500 hover:bg-sky-600 mt-2" data-testid="submit-compliance">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Invia Documenti per Verifica
          </Button>
        </div>
      )}
    </div>
  );
}
